import { MathUtils } from "../Math";
import { Timer } from "../Time/Timer";
import { ScaleManager } from "../ScaleManager";



export class DiagonalStripes {
    private _scale: ScaleManager;
    private _screenW: number;
    private _screenH: number;

    private _timer: Timer;

    private _transStartTime: number;
    private _transLen: number;
    private _transDir: number;
    private _transEase: (k: number) => number;
    private _transProgress: number;

    private _layer: pc.Entity;
    private _stripes: Stripe[];
    private _stripesColor: pc.Color[];
    private _stripesPerScreenW: number;

    /**
     * Udalost vyvolana po dokonceni prechodu
     * 
     */
    static readonly EVENT_COMPLETE = "trans:complete";

    /**
     * (RO) Handler udalosti, u ktereho se vyvolavaji zpravy
     */
    get eventHandler(): pc.EventHandler { return this._eventHandler; }
    private _eventHandler: pc.EventHandler;

    /**
     * (RO) True, pokud byla provedena inicializace
     */
    get initialized(): boolean { return !(this._stripesPerScreenW == undefined); }

    /**
     * (RO) True, pokud je prechod aktivni
     */
    get active(): boolean { return this._layer.enabled; }

    /**
     * (RO) Prubeh prechodu 0 az 1. 
     */
    get progress(): number { return this._transProgress; }

    /**
     * (RO) Smer prechodu (1 = zavreni obrazovky, -1 = otevreni obrazovky)
     */
    get dir(): number { return this._transDir; }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(screen: pc.Entity, scale: ScaleManager, timer: Timer, eventHandler?: pc.EventHandler) {
        this._timer = timer;
        this._eventHandler = eventHandler;

        this._layer = new pc.Entity("diagStripesTrans");
        this._layer.addComponent(pc.eCompomentType.element, {
            type: pc.ELEMENTTYPE_GROUP,
            anchor: [0, 1, 0, 1],
            pivot: [0, 1],
            width: 0,
            height: 0
        });
        this._layer.enabled = false;
        screen.addChild(this._layer);

        this._stripes = [];

        // Zachytavat zmenu rozliseni
        this._scale = scale;
        scale.app.on(ScaleManager.EVENT_RESIZE, this.handleScreenResize);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(stripesPerScreenW: number, stripesColor: pc.Color[]): void {
        // Pocet pruhu na sirku obrazovky
        this._stripesPerScreenW = stripesPerScreenW;

        // Barvy
        if (!this._stripesColor || this._stripesColor.length != stripesColor.length)
            this._stripesColor = [];

        stripesColor.forEach((color: pc.Color, i: number) => {
            if (i == this._stripesColor.length)
                this._stripesColor.push(new pc.Color());

            this._stripesColor[i].copy(color);
        });

        // Vytvorit pruhy
        this.createStripes();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    start(len: number, dir: number, ease: (k: number) => number = pc.Linear): void {
        if (this._screenW != this._scale.width || this._screenH != this._scale.height)
            this.createStripes();

        this._transLen = len;
        this._transStartTime = this._timer.time;
        this._transDir = dir;
        this._transEase = ease;
        this._transProgress = 0;

        this._layer.enabled = true;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._layer.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (!this.active || this._transProgress == 1)
            return;

        let progress = (this._timer.time - this._transStartTime) / this._transLen;

        if (progress >= 1)
            progress = 1;

        this._transProgress = progress;

        progress = this._transEase(progress);

        if (this._transDir < 0)
            progress = 1 - progress;

        this._stripes.forEach((stripe: Stripe) => {
            stripe.update(progress);
        });

        if (this._transProgress == 1) {
            if (this._transDir < 0)
                this.reset();

            if (this._eventHandler)
                this._eventHandler.fire(DiagonalStripes.EVENT_COMPLETE, this._transDir);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createStripes(): void {
        // Nastavit jednotlive pruhy, ze kterych se prechod sklada
        let screenW = this._screenW = this._scale.width;
        let screenH = this._screenH = this._scale.height;

        let sin45 = Math.sin(MathUtils.degToRad(45));

        let stripeDiagonalSize = screenW / this._stripesPerScreenW;
        let stripeId = 0
        let stripeX = stripeDiagonalSize;
        let stripeY = 0;
        let stripeH = stripeDiagonalSize / (2 * sin45);

        while (stripeY > -(screenH + stripeDiagonalSize / 2)) {
            // Vypocitat velikost strany B vyplnovaneho trojuhelniku
            let triangleSideA = Math.min(stripeX, screenH + stripeY + (stripeDiagonalSize / 2));
            let triangleSideB = 2 * triangleSideA * sin45;

            if (stripeId == this._stripes.length)
                this._stripes.push(new Stripe(stripeId, this._layer));

            this._stripes[stripeId].init(stripeX, stripeY, triangleSideB, stripeH, this._stripesColor[(stripeId % this._stripesColor.length)]);

            stripeId++;

            if (stripeId < this._stripesPerScreenW) {
                // Posun po ose X
                stripeX += stripeDiagonalSize;
            }
            else if (stripeId == this._stripesPerScreenW) {
                stripeX = screenW + stripeDiagonalSize / 2;
                stripeY = -stripeDiagonalSize / 2;
            }
            else {
                stripeY -= stripeDiagonalSize;
            }
        }

        // Uvolnit nadbytecne pruhy
        for (let i = stripeId; i < this._stripes.length; i++) {
            this._stripes[i].destroy();
            this._stripes[i] = undefined;
        }

        this._stripes.length = stripeId;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleScreenResize(w: number, h: number): void {
        if (this.active)
            this.createStripes();
    }
}




class Stripe {
    static readonly ROTATION_DEG = 45;
    static readonly ROTATION_RAD = MathUtils.degToRad(Stripe.ROTATION_DEG);

    private static readonly _STEP_X = Math.cos(Stripe.ROTATION_RAD);
    private static readonly _STEP_Y = Math.sin(Stripe.ROTATION_RAD);

    private _entity: pc.Entity;

    private _id: number;

    private _startX: number;
    private _startY: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(id: number, layer: pc.Entity) {
        this._id = id;

        // Vytvorit entitu
        this._entity = new pc.Entity("stripe_" + this._id);
        this._entity.addComponent(pc.eCompomentType.element, {
            type: pc.ELEMENTTYPE_IMAGE,
            pivot: [0, 0],
            anchor: [0, 1, 0, 1],
        });

        this._entity.setLocalEulerAngles(0, 0, Stripe.ROTATION_DEG);
        layer.addChild(this._entity);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(x: number, y: number, w: number, h: number, color: pc.Color): void {
        // Nastavit image element
        let element = this._entity.element;

        element.width = w;
        element.height = h;
        element.color = color;

        // Pocatecni pozice
        if ((this._id & 1) == 0) {
            this._startX = x;
            this._startY = y;
        }
        else {
            this._startX = x - Stripe._STEP_X * w * 2;
            this._startY = y - Stripe._STEP_Y * w * 2;
        }

        this._entity.setLocalPosition(this._startX, this._startY, 0);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    destroy(): void {
        if (this._entity) {
            this._entity.destroy();
            this._entity = undefined;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(progress: number): void {
        let x = (progress * this._entity.element.width);
        let y = x * Stripe._STEP_Y;

        x *= Stripe._STEP_X;


        if ((this._id & 1) == 0) {
            x = this._startX - x;
            y = this._startY - y;
        }
        else {
            x = this._startX + x;
            y = this._startY + y;
        }

        this._entity.setLocalPosition(x, y, 0);
    }
}